/**
 * NestedComments - Reddit-style nested/threaded comments
 * Supports collapsing, voting, and replying
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Reply, MoreHorizontal, Loader2, BadgeCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Vote icon themes - can be extended or made configurable per-club
const VOTE_ICON_THEMES = {
  dogs: { up: '🐕', down: '🐺', name: 'Dogs' },
  cats: { up: '😺', down: '😾', name: 'Cats' },
  paws: { up: '🐾', down: '💔', name: 'Paws' },
  hearts: { up: '❤️', down: '💔', name: 'Hearts' },
  arrows: { up: '⬆️', down: '⬇️', name: 'Arrows' },
  thumbs: { up: '👍', down: '👎', name: 'Thumbs' },
  bones: { up: '🦴', down: '💀', name: 'Bones' },
};

// Default theme - could be pulled from club settings or user preferences
const DEFAULT_VOTE_THEME = 'dogs';

// Single comment component
const Comment = ({ 
  comment, 
  depth = 0, 
  maxDepth = 4, 
  onReply, 
  currentUserId,
  postId,
  voteTheme = DEFAULT_VOTE_THEME
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replies, setReplies] = useState(comment.replies || []);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [repliesLoaded, setRepliesLoaded] = useState(!!comment.replies);
  const [voteState, setVoteState] = useState(comment.user_vote || null);
  const [score, setScore] = useState(comment.score || 0);
  const [voting, setVoting] = useState(false);

  // Get vote icons from theme
  const voteIcons = VOTE_ICON_THEMES[voteTheme] || VOTE_ICON_THEMES.dogs;

  // Calculate indent based on depth
  const indentClass = depth > 0 ? 'ml-4 pl-3 border-l-2 border-muted' : '';

  const handleVote = async (voteType) => {
    // Prevent multiple simultaneous votes
    if (voting) return;
    
    try {
      if (!currentUserId) {
        toast.error('Please sign in to vote');
        return;
      }

      setVoting(true);

      const res = await fetch(`${API_URL}/api/comments/${comment.id}/vote`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vote_type: voteType })
      });

      if (res.ok) {
        const data = await res.json();
        // Use the server's authoritative score and vote state
        setScore(data.score);
        setVoteState(data.vote_state);
      } else {
        toast.error('Failed to vote');
      }
    } catch (err) {
      toast.error('Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  const loadReplies = async () => {
    if (repliesLoaded || comment.children_count === 0) return;
    
    setLoadingReplies(true);
    try {
      const res = await fetch(`${API_URL}/api/comments/${comment.id}/replies`);
      if (res.ok) {
        const data = await res.json();
        setReplies(data);
        setRepliesLoaded(true);
      }
    } catch (err) {
      toast.error('Failed to load replies');
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) return;
    
    setSubmitting(true);
    try {
      if (!currentUserId) {
        toast.error('Please sign in to reply');
        return;
      }

      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: replyContent,
          parent_id: comment.id
        })
      });

      if (res.ok) {
        const newReply = await res.json();
        setReplies([...replies, { ...newReply, replies: [] }]);
        setReplyContent('');
        setShowReplyForm(false);
        toast.success('Reply posted');
        if (onReply) onReply();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to post reply');
      }
    } catch (err) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const canReply = depth < maxDepth;

  return (
    <div className={`${indentClass}`} data-testid={`comment-${comment.id}`}>
      <div className="py-2">
        {/* Comment header */}
        <div className="flex items-center gap-2">
          {/* Collapse toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-0.5 hover:bg-muted rounded"
            data-testid={`collapse-comment-${comment.id}`}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          
          {/* Avatar */}
          <Avatar className="w-6 h-6">
            <AvatarImage src={comment.author_avatar} />
            <AvatarFallback className="text-xs">
              {comment.author_username?.charAt(0)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          {/* Author and time */}
          <span className="text-sm font-medium">{comment.author_username}</span>
          {comment.author_verified && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <BadgeCheck className="w-4 h-4 text-blue-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Identity Verified</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>
          
          {/* Score */}
          {score !== 0 && (
            <span className={`text-xs font-medium ${score > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {score > 0 ? '+' : ''}{score}
            </span>
          )}
        </div>

        {/* Comment content (collapsible) */}
        {!isCollapsed && (
          <div className="mt-1 ml-6">
            <p className="text-sm text-foreground whitespace-pre-wrap">
              {comment.content}
            </p>
            {comment.image_url && (
              <a
                href={comment.image_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-2"
                onClick={(e) => e.stopPropagation()}
                data-testid={`comment-image-${comment.id}`}
              >
                <img
                  src={comment.image_url}
                  alt="Comment attachment"
                  className="max-w-full sm:max-w-sm max-h-72 rounded-md border object-cover hover:opacity-95 transition-opacity"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </a>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-1 mt-2">
              {/* Vote buttons - themed icons */}
              <button
                onClick={() => handleVote('up')}
                disabled={voting}
                className={`p-1 rounded hover:bg-muted transition-all text-lg ${
                  voteState === 'up' 
                    ? 'bg-green-500/10 scale-110' 
                    : 'opacity-60 grayscale hover:opacity-100 hover:grayscale-0'
                } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`upvote-comment-${comment.id}`}
                title={voteState === 'up' ? 'Remove upvote' : 'Upvote'}
              >
                {voteIcons.up}
              </button>
              <button
                onClick={() => handleVote('down')}
                disabled={voting}
                className={`p-1 rounded hover:bg-muted transition-all text-lg ${
                  voteState === 'down' 
                    ? 'bg-red-500/10 scale-110' 
                    : 'opacity-60 grayscale hover:opacity-100 hover:grayscale-0'
                } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`downvote-comment-${comment.id}`}
                title={voteState === 'down' ? 'Remove downvote' : 'Downvote'}
              >
                {voteIcons.down}
              </button>
              
              {/* Reply button */}
              {canReply && (
                <button
                  onClick={() => setShowReplyForm(!showReplyForm)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:bg-muted rounded"
                  data-testid={`reply-comment-${comment.id}`}
                >
                  <Reply className="w-3 h-3" />
                  Reply
                </button>
              )}

              {/* More options */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-muted text-muted-foreground">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => {
                    navigator.clipboard.writeText(comment.content);
                    toast.success('Copied to clipboard');
                  }}>
                    Copy text
                  </DropdownMenuItem>
                  {currentUserId === comment.author_id && (
                    <DropdownMenuItem className="text-red-500">
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Reply form */}
            {showReplyForm && (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  rows={2}
                  className="text-sm"
                  data-testid={`reply-textarea-${comment.id}`}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSubmitReply}
                    disabled={submitting || !replyContent.trim()}
                    data-testid={`submit-reply-${comment.id}`}
                  >
                    {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Reply
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowReplyForm(false);
                      setReplyContent('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Load more replies button */}
            {!repliesLoaded && comment.children_count > 0 && (
              <button
                onClick={loadReplies}
                className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
                disabled={loadingReplies}
              >
                {loadingReplies ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                Load {comment.children_count} {comment.children_count === 1 ? 'reply' : 'replies'}
              </button>
            )}

            {/* Nested replies */}
            {replies.length > 0 && (
              <div className="mt-2">
                {replies.map(reply => (
                  <Comment
                    key={reply.id}
                    comment={reply}
                    depth={depth + 1}
                    maxDepth={maxDepth}
                    onReply={onReply}
                    currentUserId={currentUserId}
                    postId={postId}
                    voteTheme={voteTheme}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Collapsed indicator */}
        {isCollapsed && replies.length > 0 && (
          <span className="ml-6 text-xs text-muted-foreground">
            ({replies.length + (comment.children_count || 0)} hidden)
          </span>
        )}
      </div>
    </div>
  );
};


// Main nested comments component
const NestedComments = ({ 
  postId, 
  comments = [], 
  maxDepth = 4,
  currentUserId,
  onCommentAdded,
  voteTheme = DEFAULT_VOTE_THEME
}) => {
  if (comments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No comments yet. Be the first to comment!
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="nested-comments">
      {comments.map(comment => (
        <Comment
          key={comment.id}
          comment={comment}
          depth={0}
          maxDepth={maxDepth}
          onReply={onCommentAdded}
          currentUserId={currentUserId}
          postId={postId}
          voteTheme={voteTheme}
        />
      ))}
    </div>
  );
};

// Export the themes for use by parent components
export { VOTE_ICON_THEMES };
export default NestedComments;
